import React, { useState, useEffect, useRef } from 'react';
import { logger } from '../lib/logger';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { 
  Car, LogOut, Calendar, Clock, CreditCard, QrCode, 
  ChevronRight, Star, User, FileText, Loader2, Smartphone, Trophy, Download,
  Edit2, X, AlertTriangle
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { reservationsApi, invoicesApi } from '../lib/api';
import api from '../lib/api';
import LoyaltyWidget from '../components/LoyaltyWidget';

const AccountPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [qrType, setQrType] = useState('CHECKIN');
  const [loyaltyPoints, setLoyaltyPoints] = useState(0);
  const [loyaltyTier, setLoyaltyTier] = useState('BRONZE');
  const qrRef = useRef(null);
  
  // New states for edit and cancel
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [editData, setEditData] = useState({});
  const [actionLoading, setActionLoading] = useState(false);

  const locale = i18n.language === 'es' ? es : enUS;

  // Function to download QR as PNG
  const downloadQR = () => {
    if (!qrRef.current || !selectedReservation) return;
    
    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    
    // Add padding and branding
    const padding = 40;
    const textHeight = 80;
    canvas.width = 200 + padding * 2;
    canvas.height = 200 + padding * 2 + textHeight;
    
    img.onload = () => {
      // White background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw QR
      ctx.drawImage(img, padding, padding, 200, 200);
      
      // Add text
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('PLEYADES PARKING', canvas.width / 2, 200 + padding + 30);
      ctx.font = '14px Arial';
      ctx.fillText(`${qrType === 'CHECKIN' ? 'CHECK-IN' : 'CHECK-OUT'} - ${selectedReservation.reservation_code}`, canvas.width / 2, 200 + padding + 55);
      ctx.font = '12px Arial';
      ctx.fillStyle = '#666666';
      ctx.fillText(selectedReservation.vehicle_plate, canvas.width / 2, 200 + padding + 75);
      
      // Download
      const link = document.createElement('a');
      link.download = `pleyades-${qrType.toLowerCase()}-${selectedReservation.reservation_code}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success(i18n.language === 'es' ? 'QR guardado en tu dispositivo' : 'QR saved to your device');
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchReservations();
    fetchLoyaltyData();
  }, [isAuthenticated, navigate]);

  const fetchLoyaltyData = async () => {
    try {
      const response = await api.get('/loyalty/profile');
      setLoyaltyPoints(response.data.available_points || 0);
      setLoyaltyTier(response.data.tier || 'BRONZE');
    } catch (error) {
      logger.error('Error fetching loyalty data:', error);
    }
  };

  const fetchReservations = async () => {
    try {
      const response = await reservationsApi.getMyReservations();
      setReservations(response.data);
    } catch (error) {
      toast.error('Error al cargar reservas');
    } finally {
      setLoading(false);
    }
  };

  const openQRDialog = async (reservation) => {
    try {
      // Get full reservation with QR tokens
      const response = await reservationsApi.getById(reservation.id);
      setSelectedReservation(response.data);
      setShowQRDialog(true);
    } catch (error) {
      toast.error('Error al cargar QR');
    }
  };

  const formatDate = (dateStr) => {
    return format(new Date(dateStr), "dd MMM yyyy 'a las' HH:mm", { locale });
  };

  const getStatusColor = (status) => {
    const colors = {
      PENDING_PAYMENT: 'bg-warning/20 text-warning',
      CONFIRMED: 'bg-success/20 text-success',
      CANCELLED: 'bg-destructive/20 text-destructive',
      COMPLETED: 'bg-primary/20 text-primary',
      NO_SHOW: 'bg-zinc-500/20 text-zinc-400'
    };
    return colors[status] || 'bg-zinc-500/20 text-zinc-400';
  };

  // Check if reservation can be modified/cancelled
  const canModifyReservation = (reservation) => {
    if (!['CONFIRMED', 'PENDING_PAYMENT'].includes(reservation.status)) return false;
    const startDate = new Date(reservation.start_dt);
    return startDate > new Date();
  };

  const canCancelReservation = (reservation) => {
    if (!['CONFIRMED', 'PENDING_PAYMENT'].includes(reservation.status)) return false;
    const startDate = new Date(reservation.start_dt);
    const hoursUntilStart = (startDate - new Date()) / (1000 * 60 * 60);
    return hoursUntilStart > 24;
  };

  // Open edit dialog
  const handleEditClick = (reservation) => {
    setSelectedReservation(reservation);
    setEditData({
      vehicle_plate: reservation.vehicle_plate || '',
      vehicle_brand: reservation.vehicle_brand || '',
      vehicle_model: reservation.vehicle_model || '',
      vehicle_color: reservation.vehicle_color || '',
      arrival_flight: reservation.arrival_flight || '',
      departure_flight: reservation.departure_flight || '',
      notes: reservation.notes || ''
    });
    setShowEditDialog(true);
  };

  // Save modifications
  const handleSaveModification = async () => {
    if (!selectedReservation) return;
    setActionLoading(true);
    try {
      await api.put(`/customer/reservations/${selectedReservation.id}`, editData);
      toast.success('Reserva modificada correctamente');
      setShowEditDialog(false);
      // Refresh reservations
      const response = await reservationsApi.getMine();
      setReservations(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al modificar la reserva');
    } finally {
      setActionLoading(false);
    }
  };

  // Open cancel dialog
  const handleCancelClick = (reservation) => {
    setSelectedReservation(reservation);
    setShowCancelDialog(true);
  };

  // Confirm cancellation
  const handleConfirmCancel = async () => {
    if (!selectedReservation) return;
    setActionLoading(true);
    try {
      await api.post(`/customer/reservations/${selectedReservation.id}/cancel`);
      toast.success('Reserva cancelada. Recibirás un email de confirmación.');
      setShowCancelDialog(false);
      // Refresh reservations
      const response = await reservationsApi.getMine();
      setReservations(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar la reserva');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass-heavy sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
              <Car className="w-6 h-6 text-black" />
            </div>
            <span className="font-heading font-bold text-xl text-white">PLEYADES</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-zinc-400">{user?.name}</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => { logout(); navigate('/'); }}
              className="text-zinc-400 hover:text-white"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          
          {/* CTA Banner - Reservar Ahora */}
          <div className="mb-8 bg-gradient-to-r from-primary via-yellow-500 to-primary p-1 rounded-xl">
            <div className="bg-black/90 backdrop-blur-sm rounded-lg p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-center sm:text-left">
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">
                  {i18n.language === 'es' ? '¿Necesitas aparcar?' : 'Need parking?'}
                </h2>
                <p className="text-zinc-400 text-sm">
                  {i18n.language === 'es' 
                    ? 'Reserva tu plaza en segundos y viaja tranquilo' 
                    : 'Book your spot in seconds and travel worry-free'}
                </p>
              </div>
              <Button 
                onClick={() => navigate('/reservar')} 
                className="btn-primary text-lg px-8 py-6 shadow-lg shadow-primary/30 hover:shadow-primary/50 transition-all hover:scale-105 whitespace-nowrap"
                data-testid="cta-reserve-btn"
              >
                <Car className="w-5 h-5 mr-2" />
                {i18n.language === 'es' ? 'RESERVAR AHORA' : 'BOOK NOW'}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>

          {/* User Card */}
          <div className="card-dark p-6 mb-8">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-black" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold text-white">{user?.name}</h1>
                <p className="text-zinc-400">{user?.email}</p>
              </div>
            </div>

            {/* Loyalty Points Summary */}
            <div className="mt-6 p-4 bg-gradient-to-r from-primary/20 to-primary/5 rounded-lg border border-primary/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Trophy className="w-6 h-6 text-primary" />
                  <div>
                    <p className="text-zinc-400 text-sm">{loyaltyTier}</p>
                    <p className="text-2xl font-bold text-primary">{loyaltyPoints} <span className="text-sm font-normal text-zinc-400">puntos</span></p>
                  </div>
                </div>
                <Button 
                  className="btn-secondary text-sm"
                  onClick={() => document.querySelector('[data-testid="tab-loyalty"]')?.click()}
                >
                  {i18n.language === 'es' ? 'Ver más' : 'See more'}
                </Button>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="reservations">
            <TabsList className="w-full grid grid-cols-3 bg-secondary mb-6">
              <TabsTrigger 
                value="reservations"
                className="data-[state=active]:bg-primary data-[state=active]:text-black"
                data-testid="tab-reservations"
              >
                <Calendar className="w-4 h-4 mr-2" />
                {t('account.reservations')}
              </TabsTrigger>
              <TabsTrigger 
                value="loyalty"
                className="data-[state=active]:bg-primary data-[state=active]:text-black"
                data-testid="tab-loyalty"
              >
                <Trophy className="w-4 h-4 mr-2" />
                {i18n.language === 'es' ? 'Fidelidad' : 'Loyalty'}
              </TabsTrigger>
              <TabsTrigger 
                value="profile"
                className="data-[state=active]:bg-primary data-[state=active]:text-black"
                data-testid="tab-profile"
              >
                <User className="w-4 h-4 mr-2" />
                {t('account.profile')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="reservations">
              {reservations.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-400 mb-4">{t('account.noReservations')}</p>
                  <Button onClick={() => navigate('/reservar')} className="btn-primary" data-testid="new-reservation-btn">
                    {t('nav.reserve')}
                    <ChevronRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {reservations.map((reservation) => (
                    <div key={reservation.id} className="card-dark p-6" data-testid={`reservation-${reservation.id}`}>
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <span className="font-mono text-lg font-bold text-primary">
                              {reservation.reservation_code}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded ${getStatusColor(reservation.status)}`}>
                              {t(`status.${reservation.status}`)}
                            </span>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                            <div className="flex items-start gap-2">
                              <Calendar className="w-4 h-4 text-primary mt-0.5" />
                              <div>
                                <p className="text-zinc-400">Entrada</p>
                                <p className="text-white">{formatDate(reservation.start_dt)}</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <Clock className="w-4 h-4 text-primary mt-0.5" />
                              <div>
                                <p className="text-zinc-400">Salida</p>
                                <p className="text-white">{formatDate(reservation.end_dt)}</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <Car className="w-4 h-4 text-primary mt-0.5" />
                              <div>
                                <p className="text-zinc-400">Vehículo</p>
                                <p className="text-white font-mono">{reservation.vehicle_plate}</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <CreditCard className="w-4 h-4 text-primary mt-0.5" />
                              <div>
                                <p className="text-zinc-400">Total</p>
                                <p className="text-white font-bold">{(reservation.total_cents / 100).toFixed(2)}€</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-col gap-2">
                          {/* QR and Invoice buttons for confirmed reservations */}
                          {reservation.status === 'CONFIRMED' && (
                            <div className="flex flex-col sm:flex-row gap-2">
                              <Button
                                onClick={() => openQRDialog(reservation)}
                                className="btn-primary"
                                data-testid={`show-qr-${reservation.id}`}
                              >
                                <QrCode className="w-4 h-4 mr-2" />
                                {t('checkout.showQR')}
                              </Button>
                              <Button
                                onClick={async () => {
                                  try {
                                    await invoicesApi.generate(reservation.reservation_code);
                                    window.open(invoicesApi.downloadPdf(reservation.reservation_code), '_blank');
                                  } catch (error) {
                                    toast.error(i18n.language === 'es' ? 'Error al generar factura' : 'Error generating invoice');
                                  }
                                }}
                                className="btn-secondary"
                                data-testid={`download-invoice-${reservation.id}`}
                              >
                                <Download className="w-4 h-4 mr-2" />
                                PDF
                              </Button>
                            </div>
                          )}
                          
                          {/* Modify and Cancel buttons */}
                          {canModifyReservation(reservation) && (
                            <div className="flex flex-col sm:flex-row gap-2 mt-2 pt-2 border-t border-white/10">
                              <Button
                                onClick={() => handleEditClick(reservation)}
                                variant="outline"
                                className="border-zinc-600 hover:bg-zinc-800 text-zinc-300"
                                data-testid={`edit-reservation-${reservation.id}`}
                              >
                                <Edit2 className="w-4 h-4 mr-2" />
                                Modificar
                              </Button>
                              {canCancelReservation(reservation) ? (
                                <Button
                                  onClick={() => handleCancelClick(reservation)}
                                  variant="outline"
                                  className="border-red-600/50 hover:bg-red-900/20 text-red-400"
                                  data-testid={`cancel-reservation-${reservation.id}`}
                                >
                                  <X className="w-4 h-4 mr-2" />
                                  Cancelar
                                </Button>
                              ) : (
                                <p className="text-xs text-zinc-500 self-center">
                                  Cancelación no disponible (&lt;24h)
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="loyalty">
              <LoyaltyWidget />
            </TabsContent>

            <TabsContent value="profile">
              <div className="card-dark p-6">
                <h2 className="font-heading text-xl font-bold text-white mb-6">{t('account.profile')}</h2>
                <div className="space-y-4">
                  <div>
                    <p className="text-zinc-400 text-sm">{t('auth.name')}</p>
                    <p className="text-white">{user?.name}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">{t('auth.email')}</p>
                    <p className="text-white">{user?.email}</p>
                  </div>
                  {user?.phone && (
                    <div>
                      <p className="text-zinc-400 text-sm">{t('auth.phone')}</p>
                      <p className="text-white">{user?.phone}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-zinc-400 text-sm">Idioma</p>
                    <p className="text-white">{user?.language === 'es' ? 'Español' : 'English'}</p>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* New Reservation CTA (footer) */}
          <div className="mt-8 text-center">
            <Button onClick={() => navigate('/reservar')} className="btn-primary" data-testid="footer-reserve-btn">
              {t('hero.cta')}
              <ChevronRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* QR Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="bg-card border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-center">
              {selectedReservation?.reservation_code}
            </DialogTitle>
          </DialogHeader>
          
          {selectedReservation && (
            <div className="space-y-6">
              <div className="flex gap-2 justify-center">
                <Button
                  onClick={() => setQrType('CHECKIN')}
                  className={qrType === 'CHECKIN' ? 'btn-primary' : 'btn-secondary'}
                  data-testid="qr-checkin-tab"
                >
                  Check-in
                </Button>
                <Button
                  onClick={() => setQrType('CHECKOUT')}
                  className={qrType === 'CHECKOUT' ? 'btn-primary' : 'btn-secondary'}
                  data-testid="qr-checkout-tab"
                >
                  Check-out
                </Button>
              </div>

              <div className="flex justify-center" ref={qrRef}>
                <div className="bg-white p-4 rounded-lg">
                  <QRCodeSVG
                    value={qrType === 'CHECKIN' 
                      ? selectedReservation.qr_checkin_token 
                      : selectedReservation.qr_checkout_token}
                    size={200}
                    level="H"
                    data-testid="qr-code"
                  />
                </div>
              </div>

              <p className="text-center text-zinc-400 text-sm">
                {qrType === 'CHECKIN' 
                  ? (i18n.language === 'es' ? 'Muestra este QR a tu conductor' : 'Show this QR to your driver')
                  : (i18n.language === 'es' ? 'Muestra este QR a tu conductor' : 'Show this QR to your driver')}
              </p>

              {/* Reminder */}
              <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg">
                <p className="text-center text-primary text-sm font-medium">
                  {i18n.language === 'es' 
                    ? '📞 Recuerda llamar 30 minutos antes de llegar al aeropuerto' 
                    : '📞 Remember to call 30 minutes before arriving at the airport'}
                </p>
              </div>

              {/* Download Button */}
              <div className="flex justify-center">
                <Button
                  onClick={downloadQR}
                  className="btn-secondary w-full"
                  data-testid="download-qr-btn"
                >
                  <Smartphone className="w-4 h-4 mr-2" />
                  {i18n.language === 'es' ? 'Guardar QR en mi dispositivo' : 'Save QR to my device'}
                </Button>
              </div>

              <div className="text-center text-sm text-zinc-500">
                <p className="font-mono">{selectedReservation.vehicle_plate}</p>
                <p>{formatDate(selectedReservation.start_dt)} - {formatDate(selectedReservation.end_dt)}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Reservation Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-secondary border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-primary" />
              Modificar Reserva
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Modifica los datos de tu reserva {selectedReservation?.reservation_code}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-white">Matrícula</Label>
              <Input
                value={editData.vehicle_plate}
                onChange={(e) => setEditData({...editData, vehicle_plate: e.target.value.toUpperCase()})}
                className="bg-background border-white/10 mt-1"
                data-testid="edit-vehicle-plate"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Marca</Label>
                <Input
                  value={editData.vehicle_brand}
                  onChange={(e) => setEditData({...editData, vehicle_brand: e.target.value})}
                  className="bg-background border-white/10 mt-1"
                />
              </div>
              <div>
                <Label className="text-white">Modelo</Label>
                <Input
                  value={editData.vehicle_model}
                  onChange={(e) => setEditData({...editData, vehicle_model: e.target.value})}
                  className="bg-background border-white/10 mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-white">Color</Label>
              <Input
                value={editData.vehicle_color}
                onChange={(e) => setEditData({...editData, vehicle_color: e.target.value})}
                className="bg-background border-white/10 mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Vuelo de llegada</Label>
                <Input
                  value={editData.arrival_flight}
                  onChange={(e) => setEditData({...editData, arrival_flight: e.target.value.toUpperCase()})}
                  className="bg-background border-white/10 mt-1"
                  placeholder="IB1234"
                />
              </div>
              <div>
                <Label className="text-white">Vuelo de salida</Label>
                <Input
                  value={editData.departure_flight}
                  onChange={(e) => setEditData({...editData, departure_flight: e.target.value.toUpperCase()})}
                  className="bg-background border-white/10 mt-1"
                  placeholder="IB5678"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-white">Notas</Label>
              <Input
                value={editData.notes}
                onChange={(e) => setEditData({...editData, notes: e.target.value})}
                className="bg-background border-white/10 mt-1"
                placeholder="Información adicional..."
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowEditDialog(false)}
              className="border-zinc-600"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveModification}
              disabled={actionLoading}
              className="btn-primary"
              data-testid="save-modification-btn"
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Edit2 className="w-4 h-4 mr-2" />
              )}
              Guardar cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Reservation Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="bg-secondary border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Cancelar Reserva
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-4">
              <p className="text-red-400 text-sm">
                ¿Estás seguro de que quieres cancelar la reserva <strong className="text-white">{selectedReservation?.reservation_code}</strong>?
              </p>
              <p className="text-red-400/70 text-xs mt-2">
                Esta acción no se puede deshacer.
              </p>
            </div>
            
            {selectedReservation && (
              <div className="bg-background rounded-lg p-3 text-sm">
                <div className="flex justify-between text-zinc-400 mb-1">
                  <span>Vehículo:</span>
                  <span className="text-white font-mono">{selectedReservation.vehicle_plate}</span>
                </div>
                <div className="flex justify-between text-zinc-400 mb-1">
                  <span>Entrada:</span>
                  <span className="text-white">{selectedReservation.start_dt && formatDate(selectedReservation.start_dt)}</span>
                </div>
                <div className="flex justify-between text-zinc-400">
                  <span>Salida:</span>
                  <span className="text-white">{selectedReservation.end_dt && formatDate(selectedReservation.end_dt)}</span>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowCancelDialog(false)}
              className="border-zinc-600"
            >
              Volver
            </Button>
            <Button
              onClick={handleConfirmCancel}
              disabled={actionLoading}
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-cancel-btn"
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <X className="w-4 h-4 mr-2" />
              )}
              Confirmar cancelación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountPage;
